import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CameraIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { badgeClass, getApiErrorMessage, inputClass, primaryButtonClass, secondaryButtonClass } from '../../components/ui/FormControls';
import { cn } from '../../lib/utils';

interface ScanMatch {
  kind: string;
  kindLabel: string;
  id: string;
  code: string;
  title: string;
  details: { label: string; value: string }[];
  link: string;
}

interface ScanResult {
  code: string;
  matches: ScanMatch[];
}

// The browser's own barcode reader, where there is one (Chrome on Android, for example)
interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeReader {
  detect(source: HTMLVideoElement): Promise<DetectedBarcode[]>;
}
type BarcodeReaderClass = new (options?: { formats?: string[] }) => BarcodeReader;
const barcodeReaderClass = (): BarcodeReaderClass | null =>
  (typeof window !== 'undefined' && (window as unknown as { BarcodeDetector?: BarcodeReaderClass }).BarcodeDetector) || null;

const KIND_TONE: Record<string, string> = {
  product: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20',
  batch: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  serial: 'bg-purple-50 text-purple-700 ring-purple-600/20',
};

// Reads codes with the camera until one is found, then hands it over
const CameraReader: React.FC<{ onCode: (code: string) => void; onClose: () => void }> = ({ onCode, onClose }) => {
  const video = useRef<HTMLVideoElement>(null);
  const [problem, setProblem] = useState('');

  useEffect(() => {
    const Reader = barcodeReaderClass();
    if (!Reader) return undefined;
    let stream: MediaStream | null = null;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const reader = new Reader({ formats: ['code_128', 'qr_code', 'ean_13', 'ean_8', 'upc_a', 'code_39'] });
    const look = async () => {
      if (stopped || !video.current) return;
      try {
        const found = await reader.detect(video.current);
        if (found.length > 0 && found[0].rawValue) {
          onCode(found[0].rawValue);
          return;
        }
      } catch {
        // the frame was not ready; try the next one
      }
      timer = setTimeout(look, 250);
    };
    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: 'environment' } })
      .then((media) => {
        stream = media;
        if (video.current) {
          video.current.srcObject = media;
          video.current.play().then(look).catch(() => setProblem('The camera could not be started.'));
        }
      })
      .catch(() => setProblem('The camera could not be opened. Allow this site to use the camera, or type the code.'));
    return () => {
      stopped = true;
      clearTimeout(timer);
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [onCode]);

  return (
    <div className="rounded-lg bg-black p-2">
      {problem ? (
        <p className="p-6 text-center text-sm text-white">{problem}</p>
      ) : (
        <video ref={video} muted playsInline className="mx-auto max-h-80 w-full rounded object-contain" />
      )}
      <div className="mt-2 flex justify-end">
        <button type="button" onClick={onClose} className="rounded-md bg-white/10 px-3 py-1.5 text-sm font-medium text-white hover:bg-white/20">
          <XMarkIcon className="-ml-0.5 mr-1 inline h-4 w-4" />
          Stop camera
        </button>
      </div>
    </div>
  );
};

const Scan: React.FC = () => {
  const input = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState('');
  const [camera, setCamera] = useState(false);
  const [history, setHistory] = useState<ScanResult[]>([]);
  const canUseCamera = !!barcodeReaderClass() && !!navigator.mediaDevices;

  const lookUp = useMutation({
    mutationFn: async (value: string) => (await api.get<ScanResult>('/scan', { params: { code: value } })).data,
    onSuccess: (result) => {
      setHistory((previous) => [result, ...previous.filter((item) => item.code !== result.code)].slice(0, 10));
      setCode('');
      input.current?.focus();
    },
  });

  // A scanner that types into the page ends with Enter, so the box is always ready for the next scan
  useEffect(() => {
    input.current?.focus();
  }, []);

  const submit = (value: string) => {
    const trimmed = value.trim();
    if (trimmed) lookUp.mutate(trimmed);
  };

  const latest = history[0];

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Scan</h1>
        <p className="mt-1 text-sm text-gray-500">Scan a label with a barcode scanner or the camera, or type the code: an item, a batch, a serial number, a tool, an instrument, a machine, a warehouse or a document number.</p>
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit(code);
        }}
        className="flex flex-col gap-3 sm:flex-row"
      >
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            ref={input}
            type="text"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder="Scan or type a code"
            aria-label="Code"
            autoComplete="off"
            maxLength={200}
            className={cn(inputClass, 'py-3 pl-10 text-lg font-mono')}
          />
        </div>
        <button type="submit" disabled={lookUp.isPending || !code.trim()} className={primaryButtonClass}>
          {lookUp.isPending ? 'Looking…' : 'Look Up'}
        </button>
        {canUseCamera && !camera && (
          <button type="button" onClick={() => setCamera(true)} className={secondaryButtonClass}>
            <CameraIcon className="-ml-0.5 mr-2 h-5 w-5" />
            Camera
          </button>
        )}
      </form>

      {camera && (
        <CameraReader
          onCode={(value) => {
            setCamera(false);
            submit(value);
          }}
          onClose={() => setCamera(false)}
        />
      )}

      {lookUp.isError && <p className="text-sm text-red-600">{getApiErrorMessage(lookUp.error, 'The code could not be looked up')}</p>}

      {latest && (
        <section aria-live="polite" data-testid="scan-result">
          <h2 className="text-sm font-medium text-gray-500">
            <span className="font-mono text-gray-900">{latest.code}</span>
            {latest.matches.length === 0 ? ' is not known' : latest.matches.length === 1 ? ' is' : ` is one of ${latest.matches.length} things`}
          </h2>
          {latest.matches.length === 0 ? (
            <p className="mt-3 rounded-lg bg-white p-6 text-center text-sm text-gray-500 shadow-sm ring-1 ring-gray-200">
              Nothing you may see has this code. Check that the whole label was scanned.
            </p>
          ) : (
            <div className="mt-3 space-y-3">
              {latest.matches.map((match) => (
                <div key={`${match.kind}-${match.id}`} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200" data-testid="scan-match">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <span className={cn(badgeClass, 'ring-1 ring-inset', KIND_TONE[match.kind] ?? 'bg-gray-50 text-gray-700 ring-gray-500/20')}>{match.kindLabel}</span>
                      <p className="mt-2 font-mono text-lg font-semibold text-gray-900">{match.code}</p>
                      <p className="text-sm text-gray-700">{match.title}</p>
                    </div>
                    <Link to={match.link} className={secondaryButtonClass}>Go to {match.kindLabel.toLowerCase()}s</Link>
                  </div>
                  {match.details.length > 0 && (
                    <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
                      {match.details.map((detail) => (
                        <div key={detail.label}>
                          <dt className="text-xs uppercase tracking-wide text-gray-500">{detail.label}</dt>
                          <dd className="text-gray-900">{detail.value}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {history.length > 1 && (
        <section>
          <h2 className="text-sm font-medium text-gray-500">Scanned before</h2>
          <ul className="mt-2 divide-y divide-gray-100 rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
            {history.slice(1).map((item) => (
              <li key={item.code} className="flex items-center justify-between px-4 py-2 text-sm">
                <button type="button" onClick={() => submit(item.code)} className="font-mono text-indigo-600 hover:text-indigo-800">{item.code}</button>
                <span className="text-gray-500">{item.matches.length === 0 ? 'Not known' : item.matches.map((match) => match.kindLabel).join(', ')}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default Scan;
